import { ShipmentOrderLegRunForPlanEntity } from 'src/entities/ShipmentOrderLegRun.entity';

/** 荷主名が引けない案件のまとめ先。落とすと内訳の合計が地点の合計と合わなくなる */
export const UNKNOWN_SHIPPER_LABEL = '荷主不明';

export type ShipperBreakdownRow = {
  shipperName: string;
  itemCount: number;
  weightKg: number;
  volumeM3: number;
  /** distinct な shipment_order_id 数（adapter の countInvoices と同じ規則） */
  invoiceCount: number;
  /**
   * この荷主のぶんを他車両へ移すときに動かす legRun (logposeTech/log_web_issues#326)。
   * id は entity 上 optional なので、引けないものは入れない
   * （adapter.ts の buildDeliveryCard の legRunIds と同じ規則）。
   */
  legRunIds: number[];
};

/**
 * 地点の数量を荷主ごとに分けたもの。
 *
 * 同一地点に複数の荷主が混ざると、合計だけでは「どの荷主のぶんが何個か」が読めない。
 * 荷主名のタグは既に出ているが、数量が紐づいていなかった。
 *
 * 並びは数量の多い順、同数なら荷主名順。描き直しのたびに入れ替わらないよう、
 * 同数のときの順序まで決めておく。
 */
export function buildShipperBreakdown(
  legRuns: readonly ShipmentOrderLegRunForPlanEntity[],
): ShipperBreakdownRow[] {
  const byShipper = new Map<string, ShipperBreakdownRow & { orderIds: Set<number> }>();

  legRuns.forEach((legRun) => {
    const shipperName = legRun.shipper_name || UNKNOWN_SHIPPER_LABEL;
    const row = byShipper.get(shipperName) ?? {
      shipperName, itemCount: 0, weightKg: 0, volumeM3: 0, invoiceCount: 0, legRunIds: [], orderIds: new Set<number>(),
    };
    row.itemCount += legRun.item_count ?? 0;
    row.weightKg += legRun.item_total_weight_kg ?? 0;
    row.volumeM3 += legRun.item_total_volume_m3 ?? 0;
    if (typeof legRun.shipment_order_id === 'number') row.orderIds.add(legRun.shipment_order_id);
    if (typeof legRun.id === 'number') row.legRunIds.push(legRun.id);
    byShipper.set(shipperName, row);
  });

  return [...byShipper.values()]
    .map(({ orderIds, ...row }) => ({ ...row, invoiceCount: orderIds.size }))
    .sort((a, b) => b.itemCount - a.itemCount || a.shipperName.localeCompare(b.shipperName, 'ja'));
}
